import { Input as BaseInput } from '@base-ui/react/input'
import React, { useImperativeHandle, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { Close } from 'sutra/components/Icons'
import {
  Adornment,
  ClearButton,
  InnerInput,
  Wrapper,
} from 'sutra/components/Input/input-styles'
import { accents, brand, gray } from 'sutra/tokens/colors'
import { motion } from 'sutra/tokens/motion'
import { radii } from 'sutra/tokens/radii'

export type InputSize = 'xs' | 'sm' | 'md' | 'lg'

export interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'prefix'
> {
  $error?: boolean
  $size?: InputSize
  $height?: string
  // Shows a clear button while the field has a value; clicking it fires
  // `onChange` with an empty value and refocuses the input.
  allowClear?: boolean
  // Fires after the clear button's `onChange`, so a caller can tell a clear
  // from the user deleting the last character.
  onClear?: () => void
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  // Fires on Enter, except while an IME composition is confirming.
  onPressEnter?: React.KeyboardEventHandler<HTMLInputElement>
}

type InputImplProps = Omit<InputProps, '$error' | '$size' | '$height'>

// Clears through the native value setter plus a bubbling `input` event so
// React's own `onChange` fires for controlled and uncontrolled fields alike.
function clearInput(input: HTMLInputElement): void {
  Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    'value',
  )?.set?.call(input, '')
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.focus()
}

// `className` and `style` always land on the bordered element: the <input>
// itself, or the wrapper once an adornment or clear button is present.
const InputImpl = React.forwardRef<HTMLInputElement, InputImplProps>(
  function InputImpl(
    {
      className,
      style,
      prefix,
      suffix,
      allowClear,
      onClear,
      onPressEnter,
      onKeyDown,
      onChange,
      ...rest
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null)
    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement)
    const [typedValue, setTypedValue] = useState(
      String(rest.defaultValue ?? ''),
    )

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
      setTypedValue(event.target.value)
      onChange?.(event)
    }
    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
      onKeyDown?.(event)
      if (
        event.key === 'Enter' &&
        !event.nativeEvent.isComposing &&
        event.keyCode !== 229
      ) {
        onPressEnter?.(event)
      }
    }
    const fieldProps = {
      ...rest,
      ref: inputRef,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
    }

    if (!prefix && !suffix && !allowClear) {
      return <BaseInput {...fieldProps} className={className} style={style} />
    }

    const currentValue =
      rest.value === undefined ? typedValue : String(rest.value)
    const showClear =
      allowClear && currentValue !== '' && !rest.disabled && !rest.readOnly
    return (
      <Wrapper
        className={className}
        style={style}
        data-disabled={rest.disabled ? '' : undefined}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            event.preventDefault()
            inputRef.current?.focus()
          }
        }}
      >
        {prefix && <Adornment>{prefix}</Adornment>}
        <InnerInput {...fieldProps} />
        {showClear && (
          <ClearButton
            type="button"
            aria-label="Clear"
            tabIndex={-1}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              if (!inputRef.current) return
              clearInput(inputRef.current)
              onClear?.()
            }}
          >
            <Close />
          </ClearButton>
        )}
        {suffix && <Adornment>{suffix}</Adornment>}
      </Wrapper>
    )
  },
)

const sizeStyles = {
  xs: css`
    height: 24px;
    font-size: 14px;
    padding: 0 8px;
  `,
  sm: css`
    height: 36px;
    font-size: 14px;
    padding: 0 12px;
  `,
  md: css`
    height: 40px;
    font-size: 14px;
    padding: 0 14px;
  `,
  lg: css`
    height: 48px;
    font-size: 16px;
    padding: 0 16px;
  `,
}

// Base UI's Input is a native <input> that joins an enclosing Field.Root, so
// Field.Label / Field.Description / validation wire up to it automatically.
export const Input = styled(InputImpl)<InputProps>`
  border-width: 1.5px;
  border-style: solid;
  border-color: ${({ $error }) =>
    $error ? accents.crimsonBloom[500] : '#D5D7DA'};
  border-radius: ${radii.sm}px;
  background: ${brand.white};
  font-family: 'Poppins', system-ui, sans-serif;
  color: ${brand.stillNight[800]};
  width: 100%;
  box-sizing: border-box;
  transition:
    background-color ${motion.duration.base}ms ${motion.ease},
    border-color ${motion.duration.base}ms ${motion.ease},
    box-shadow ${motion.duration.base}ms ${motion.ease};

  ${({ $size = 'md' }) => sizeStyles[$size]}

  ${({ $height }) =>
    $height &&
    css`
      height: ${$height};
    `}

  &::placeholder {
    color: #a4a7ae;
  }

  &:hover:not(:disabled):not([data-disabled]):not(:focus-within) {
    background: ${gray[100]};
  }

  &:focus-within {
    outline: none;
    border-color: ${brand.stillNight[800]};
    box-shadow: 0 0 0 4px rgba(40, 54, 79, 0.16);
  }

  &:disabled,
  &[data-disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

Input.displayName = 'Input'
